import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';

const appLayoutContext = createContext<{
    sideBar: ReactNode | null;
    setSidebar: (sideBar: ReactNode | null) => void;
    topBar: ReactNode | null;
    setTopBar: (topBar: ReactNode | null) => void;
}>({
    sideBar: null,
    setSidebar: () => {},
    topBar: null,
    setTopBar: () => {},
});

export default function AppLayout({
    children,
    className = '',
    ['data-test-component']: testComponent = 'layout/DefaultLayout',
}: { children: ReactNode; className?: string } & DataTestAttributes) {
    const [sideBar, setSidebar] = useState<ReactNode | null>(null);
    const [topBar, setTopBar] = useState<ReactNode | null>(null);
    return (
        <appLayoutContext.Provider value={{ sideBar, setSidebar, topBar, setTopBar }}>
            <div
                className={classNames({ 'layout-app': true, [className]: Boolean(className) })}
                data-test-component={testComponent}>
                {sideBar}
                {topBar}
                <div
                    className='layout-app-content'
                    data-test-component='layout/MainContent'
                    data-test-screenshot-root={true}>
                    {children}
                </div>
            </div>
        </appLayoutContext.Provider>
    );
}

function useAppLayout() {
    return useContext(appLayoutContext);
}

AppLayout.Sidebar = function Sidebar({ children }: { children: ReactNode }) {
    const { setSidebar } = useAppLayout();

    useEffect(() => {
        setSidebar(<div className='layout-app-sidebar'>{children}</div>);
        return () => setSidebar(null);
    }, [children, setSidebar]);

    return null;
};

AppLayout.TopBar = function TopBar({ children }: { children: ReactNode }) {
    const { setTopBar } = useAppLayout();

    useEffect(() => {
        setTopBar(<div className='layout-app-top-bar'>{children}</div>);
        return () => setTopBar(null);
    }, [children, setTopBar]);

    return null;
};
